import React, { useState, useEffect } from 'react';
import { X, Phone, MessageCircle, Send } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';

export const SmartInquiryWidget = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    serviceType: '',
    message: ''
  });

  useEffect(() => {
    // Show widget after 5 seconds of page load
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 5000);

    return () => clearTimeout(timer);
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

    try {
      const response = await fetch(`${BACKEND_URL}/api/inquiries`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: 'inquiry@roofsnwalls.com', // Default email since it's required
          phone: formData.phone,
          serviceType: formData.serviceType,
          message: formData.message
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit inquiry');
      }

      toast.success('Thank you! We will contact you within 24 hours.');
      setFormData({
        name: '',
        phone: '',
        serviceType: '',
        message: ''
      });
      setIsFormOpen(false);
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      toast.error('Failed to submit. Please call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleWhatsAppClick = () => {
    window.open('https://wa.me/917987676517?text=Hi, I need waterproofing services', '_blank');
  };

  const handleCallClick = () => {
    window.location.href = 'tel:+917987676517';
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Floating Action Buttons */}
      {!isFormOpen && (
        <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-3">
          {/* Quick Inquiry Button */}
          <button
            onClick={() => setIsFormOpen(true)}
            className="bg-slate-800 text-white p-4 rounded-full shadow-lg hover:bg-slate-900 transition-all hover:scale-110 group"
            title="Quick Inquiry"
          >
            <MessageCircle className="w-6 h-6" />
            <span className="absolute right-full mr-3 top-1/2 -translate-y-1/2 bg-slate-800 text-white text-sm px-3 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
              Quick Inquiry
            </span>
          </button>

          {/* WhatsApp Button */}
          <button
            onClick={handleWhatsAppClick}
            className="bg-green-500 text-white p-4 rounded-full shadow-lg hover:bg-green-600 transition-all hover:scale-110 group"
            title="WhatsApp"
          >
            <MessageCircle className="w-6 h-6" />
            <span className="absolute right-full mr-3 top-1/2 -translate-y-1/2 bg-green-500 text-white text-sm px-3 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
              WhatsApp Us
            </span>
          </button>

          {/* Call Button */}
          <button
            onClick={handleCallClick}
            className="bg-blue-500 text-white p-4 rounded-full shadow-lg hover:bg-blue-600 transition-all hover:scale-110 group"
            title="Call Now"
          >
            <Phone className="w-6 h-6" />
            <span className="absolute right-full mr-3 top-1/2 -translate-y-1/2 bg-blue-500 text-white text-sm px-3 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
              Call Now
            </span>
          </button>
        </div>
      )}

      {/* Inquiry Form Modal */}
      {isFormOpen && (
        <div className="fixed bottom-6 right-6 z-50 w-96 max-w-[calc(100vw-3rem)]">
          <div className="bg-white rounded-lg shadow-2xl border border-slate-200">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-slate-200 bg-slate-50">
              <h3 className="font-semibold text-slate-900">Quick Inquiry</h3>
              <button
                onClick={() => setIsFormOpen(false)}
                className="text-slate-500 hover:text-slate-700 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div>
                <label htmlFor="widget-name" className="block text-sm font-medium text-slate-700 mb-1">
                  Name *
                </label>
                <Input
                  type="text"
                  id="widget-name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="Your name"
                  className="w-full"
                />
              </div>

              <div>
                <label htmlFor="widget-phone" className="block text-sm font-medium text-slate-700 mb-1">
                  Phone Number *
                </label>
                <Input
                  type="tel"
                  id="widget-phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  placeholder="+91 98765 43210"
                  className="w-full"
                />
              </div>

              <div>
                <label htmlFor="widget-service" className="block text-sm font-medium text-slate-700 mb-1">
                  Service Required *
                </label>
                <select
                  id="widget-service"
                  name="serviceType"
                  value={formData.serviceType}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500 bg-white text-sm"
                >
                  <option value="">Select service</option>
                  <option value="terrace">Terrace Waterproofing</option>
                  <option value="basement">Basement & Foundation</option>
                  <option value="bathroom">Bathroom Waterproofing</option>
                  <option value="external">External Walls</option>
                  <option value="tank">Water Tank & Sump</option>
                  <option value="government">Government Project</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label htmlFor="widget-message" className="block text-sm font-medium text-slate-700 mb-1">
                  Message (Optional)
                </label>
                <Textarea
                  id="widget-message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Brief description of your requirement..."
                  className="w-full text-sm"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-slate-800 hover:bg-slate-900 text-white"
              >
                {isSubmitting ? 'Submitting...' : (
                  <>
                    Submit Inquiry
                    <Send className="ml-2 w-4 h-4" />
                  </>
                )}
              </Button>

              <div className="text-center">
                <p className="text-xs text-slate-500 mb-2">Or reach us directly:</p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleWhatsAppClick}
                    className="flex-1 px-3 py-2 bg-green-50 text-green-700 rounded text-xs font-medium hover:bg-green-100 transition-colors"
                  >
                    WhatsApp
                  </button>
                  <button
                    type="button"
                    onClick={handleCallClick}
                    className="flex-1 px-3 py-2 bg-blue-50 text-blue-700 rounded text-xs font-medium hover:bg-blue-100 transition-colors"
                  >
                    Call Now
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};
